<template>
  <agent-default>
    <div class="tw-w-full">
      <div class="tw-px-4 md:tw-px-8 tw-pt-6 tw-pb-10">

        <!-- Page Header -->
        <header class="page-header tw-mb-6">
          <div>
            <h2 class="page-title">Commissions</h2>
            <p class="page-subtitle">Track your earnings, net commissions and payment history.</p>
          </div>
        </header>

        <!-- Summary Totals Strip -->
        <div class="totals-strip tw-mb-6" v-if="!loading">
          <!-- <div class="total-pill">
            <span class="total-pill__label">Gross Premium</span>
            <span class="total-pill__value">{{ formatCurrency(totals.grossPremium) }}</span>
          </div> -->
          <div class="total-pill">
            <span class="total-pill__label">Gross Commission</span>
            <span class="total-pill__value">{{ formatCurrency(totals.commission) }}</span>
          </div>
          <div class="total-pill">
            <span class="total-pill__label">WH Tax</span>
            <span class="total-pill__value">{{ formatCurrency(totals.whTax) }}</span>
          </div>
          <div class="total-pill">
            <span class="total-pill__label">Net Commission</span>
            <span class="total-pill__value">{{ formatCurrency(totals.commisNet) }}</span>
          </div>
          <!-- <div class="total-pill">
            <span class="total-pill__label">Commissions Payable</span>
            <span class="total-pill__value">{{ formatCurrency(agentSummary ? agentSummary.commissionsPayable : totals.payable) }}</span>
          </div> -->
        </div>

        <!-- Filters Row -->
        <div class="filters-card tw-mb-5">
          <div class="filters-row">
            <div class="filter-group">
              <label class="filter-label">From Date</label>
              <div class="date-input-wrapper">
                <v-icon size="18" color="#94A3B8" class="date-icon">mdi-calendar</v-icon>
                <input
                  v-model="dateRange.from"
                  type="date"
                  class="date-input"
                />
              </div>
            </div>

            <div class="filter-group">
              <label class="filter-label">To Date</label>
              <div class="date-input-wrapper">
                <v-icon size="18" color="#94A3B8" class="date-icon">mdi-calendar</v-icon>
                <input
                  v-model="dateRange.to"
                  type="date"
                  class="date-input"
                />
              </div>
            </div>

            <div class="filter-group">
              <label class="filter-label">Status</label>
              <select v-model="commissionStatus" class="status-select">
                <option value="Paid">Paid</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            <button class="btn-apply" @click="applyFilter" :disabled="loading">
              <v-icon v-if="loading" size="16" class="tw-mr-1" color="white">mdi-loading</v-icon>
              <v-icon v-else size="16" class="tw-mr-1" color="white">mdi-filter</v-icon>
              Apply Filter
            </button>

            <button
              v-if="filteredCommissions.length > 0"
              class="btn-export-pdf tw-ml-2"
              @click="exportToPdf"
              :disabled="loading"
            >
              <v-icon size="16" class="tw-mr-1" color="white">mdi-file-pdf-box</v-icon>
              Export to PDF
            </button>

            <!-- Excel Export -->
            <vue-json-to-csv
              v-if="filteredCommissions.length > 0"
              :json-data="filteredCommissions"
              :csv-title="`Commissions_Report_${appliedFilter.from}_to_${appliedFilter.to}`"
              :labels="{
                clientName: { title: 'Client Name' },
                policyNo: { title: 'Policy No' },
                invoiceNo: { title: 'Invoice No' },
                receiptNo: { title: 'Receipt No' },
                receiptDate: { title: 'Receipt Date' },
                commission: { title: 'Commission (Ksh)' },
                whTax: { title: 'WH Tax (Ksh)' },
                commisNet: { title: 'Net Commission (Ksh)' },
                chequeDate: { title: 'Cheque Date' }
              }"
            >
              <button class="btn-export-excel tw-ml-2" :disabled="loading">
                <v-icon size="16" class="tw-mr-1" color="white">mdi-file-excel-box</v-icon>
                Export to Excel
              </button>
            </vue-json-to-csv>
          </div>

          <!-- Search -->
          <div class="search-wrapper">
            <v-icon class="search-icon" color="#94A3B8" size="20">mdi-magnify</v-icon>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by client name..."
              class="search-input"
            />
            <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''">
              <v-icon size="16" color="#94A3B8">mdi-close</v-icon>
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="commissions-loading">

          <!-- Summary Totals Shimmer -->
          <div class="totals-strip tw-mb-6">
            <div
              v-for="n in 3"
              :key="`commission-total-shimmer-${n}`"
              class="commission-total-shimmer"
            >
              <div class="shimmer shimmer-total-label"></div>
              <div class="shimmer shimmer-total-value"></div>
            </div>
          </div>

          <!-- Commission Table Shimmer -->
          <div class="table-card commission-shimmer-card">
            <div class="commission-shimmer-table">

              <!-- Header -->
              <div class="commission-shimmer-header">
                <div class="shimmer shimmer-header-client"></div>
                <div class="shimmer shimmer-header-policy"></div>
                <div class="shimmer shimmer-header-invoice"></div>
                <div class="shimmer shimmer-header-receipt"></div>
                <div class="shimmer shimmer-header-date"></div>
                <div class="shimmer shimmer-header-amount"></div>
                <div class="shimmer shimmer-header-amount"></div>
                <div class="shimmer shimmer-header-amount"></div>
                <div class="shimmer shimmer-header-date"></div>
              </div>

              <!-- Rows -->
              <div
                v-for="n in 6"
                :key="`commission-shimmer-${n}`"
                class="commission-shimmer-row"
              >
                <div class="shimmer shimmer-client"></div>
                <div class="shimmer shimmer-policy"></div>
                <div class="shimmer shimmer-invoice"></div>
                <div class="shimmer shimmer-receipt"></div>
                <div class="shimmer shimmer-date"></div>
                <div class="shimmer shimmer-amount"></div>
                <div class="shimmer shimmer-amount"></div>
                <div class="shimmer shimmer-amount"></div>
                <div class="shimmer shimmer-date"></div>
              </div>

              <!-- Totals Footer -->
              <div class="commission-shimmer-footer">
                <div class="shimmer shimmer-footer-label"></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div class="shimmer shimmer-footer-value"></div>
                <div class="shimmer shimmer-footer-value"></div>
                <div class="shimmer shimmer-footer-value"></div>
                <div></div>
              </div>

            </div>
          </div>

        </div>

        <!-- Table -->
        <div v-else class="table-card">
          <div class="table-scroll" v-if="filteredCommissions.length > 0">
            <table class="comm-table">
              <thead>
                <tr>
                  <th>Client Name</th>
                  <th>Policy No</th>
                  <th>Invoice No</th>
                  <th>Receipt No</th>
                  <th>Receipt Date</th>
                  <th class="tw-text-right">Commission</th>
                  <th class="tw-text-right">WH Tax</th>
                  <th class="tw-text-right">Net Commission</th>
                  <th>Cheque Date</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in filteredCommissions"
                  :key="row.id || i"
                  class="comm-row"
                >
                  <td><span class="client-name">{{ row.clientName }}</span></td>
                  <td><span class="mono-text">{{ row.policyNo }}</span></td>
                  <td><span class="mono-text">{{ row.invoiceNo }}</span></td>
                  <td><span class="mono-text">{{ row.receiptNo }}</span></td>
                  <td class="cell-muted">{{ row.receiptDate }}</td>
                  <td class="tw-text-right">
                    <span class="commission-value">{{ formatCurrency(row.commission) }}</span>
                  </td>
                  <td class="tw-text-right">
                    <span class="tax-value">{{ formatCurrency(row.whTax) }}</span>
                  </td>
                  <td class="tw-text-right">
                    <span class="net-value">{{ formatCurrency(row.commisNet) }}</span>
                  </td>
                  <td class="cell-muted">{{ row.chequeDate || '—' }}</td>
                </tr>
              </tbody>

              <!-- Totals Footer Row -->
              <tfoot>
                <tr class="totals-row">
                  <td colspan="5" class="totals-label">Totals</td>
                  <td class="tw-text-right totals-cell">{{ formatCurrency(totals.commission) }}</td>
                  <td class="tw-text-right totals-cell">{{ formatCurrency(totals.whTax) }}</td>
                  <td class="tw-text-right totals-cell totals-cell--net">{{ formatCurrency(totals.commisNet) }}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- Empty State -->
          <div v-else class="empty-state">
            <div class="empty-icon-wrap">
              <v-icon color="#00AEEF" size="40">mdi-currency-usd</v-icon>
            </div>
            <p class="empty-title">{{ fetchError ? 'Error loading records' : 'No records found' }}</p>
            <p class="empty-subtitle">{{ fetchError || 'Try adjusting the date range or search term.' }}</p>
          </div>
        </div>

      </div>
    </div>
  </agent-default>
</template>

<script>
import AgentDefault from '@/components/layout/AgentDefault';
import axios from 'axios';
import VueJsonToCsv from 'vue-json-to-csv';
import { trackAgentInteraction } from '@/utils/agentAnalytics';

export default {
  name: 'AgentCommissions',
  components: { AgentDefault, VueJsonToCsv },
  data() {
    const y = new Date().getFullYear();
    const dateFrom = `${y}-01-01`;
    const dateTo = `${y}-12-31`;
    return {
      loading: true,
      fetchError: '',
      searchQuery: '',
      commissionStatus: 'Paid',
      dateRange: { from: dateFrom, to: dateTo },
      appliedFilter: { from: dateFrom, to: dateTo, commissionStatus: 'Paid' },
      commissions: [],
      apiTotals: { commission: 0, whTax: 0, commisNet: 0 },
    };
  },
  computed: {
    filteredCommissions() {
      let rows = this.commissions;
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        rows = rows.filter((row) => (row.clientName || '').toLowerCase().includes(q));
      }
      return rows;
    },
    totals() {
      return {
        commission: this.apiTotals.commission,
        whTax: this.apiTotals.whTax,
        commisNet: this.apiTotals.commisNet,
      };
    },
  },
  mounted() {
    this.syncStatusFromQuery();
    this.applyFilter();
  },
  watch: {
    '$route.query.pAuth': function onAuthQueryChanged() {
      this.syncStatusFromQuery();
      this.applyFilter();
    },
  },
  methods: {
    formatCurrency(value) {
      if (value == null || value === '') return '—';
      const num = Number(value);
      if (Number.isNaN(num)) return value;
      return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 }).format(num);
    },
    syncStatusFromQuery() {
      const pAuth = this.$route.query?.pAuth;
      if (pAuth === 'Y') this.commissionStatus = 'Paid';
      else if (pAuth === 'P') this.commissionStatus = 'Pending';
    },
    applyFilter() {
      trackAgentInteraction(this, 'commissions_apply_filter', {
        status: this.commissionStatus,
        from_date: this.dateRange.from,
        to_date: this.dateRange.to,
      });
      this.appliedFilter.from = this.dateRange.from;
      this.appliedFilter.to = this.dateRange.to;
      this.appliedFilter.commissionStatus = this.commissionStatus;
      this.fetchCommissions();
    },
    toDdMmYyyy(isoDate) {
      if (!isoDate) return '';
      const [y, m, d] = String(isoDate).split('-');
      return `${d || '01'}-${m || '01'}-${y || new Date().getFullYear()}`;
    },
    async getAgentCode() {
      const user = this.$store?.getters?.['auth/authenticatedUser'];
      const email = user?.email;
      const cacheKey = email ? `agentCodeCommissions_${email}` : 'agentCodeCommissions';
      const cached = localStorage.getItem(cacheKey);
      if (cached) return cached;
      const cachedNumeric = localStorage.getItem('agentNumericId');
      if (cachedNumeric) {
        localStorage.setItem(cacheKey, cachedNumeric);
        return cachedNumeric;
      }
      const agnCodeParam = user?.['custom:agent_code'];
      if (!agnCodeParam && !email) return null;
      try {
        const baseUrl = '/customer/gis/agent/dashboard/summary';
        const url = agnCodeParam
          ? `${baseUrl}?agn_code=${agnCodeParam}`
          : `${baseUrl}?email=${encodeURIComponent(email)}`;
        const res = await axios.get(url, { validateStatus: () => true, _skipErrorRedirect: true });
        if (res.status === 200 && res.data) {
          const code = res.data?.agent?.code;
          if (code != null) {
            const val = String(code);
            localStorage.setItem(cacheKey, val);
            return val;
          }
        }
      } catch {
        // fallback to custom:agent_code
      }
      if (agnCodeParam) return String(agnCodeParam);
      return null;
    },
    async fetchCommissions() {
      this.loading = true;
      this.fetchError = '';
      try {
        const agnCode = await this.getAgentCode();
        if (!agnCode) {
          this.commissions = [];
          this.apiTotals = { commission: 0, whTax: 0, commisNet: 0 };
          this.fetchError = 'Agent code not found.';
          return;
        }

        const params = {
          agnCode,
          pAuth: this.appliedFilter.commissionStatus === 'Pending' ? 'P' : 'Y',
          dateFrom: this.toDdMmYyyy(this.appliedFilter.from),
          dateTo: this.toDdMmYyyy(this.appliedFilter.to),
          limit: 1000,
        };

        const res = await axios.get('/customer/gis/agent/commission-payments', {
          params,
          validateStatus: () => true,
          _skipErrorRedirect: true,
        });

        if (res.status === 200 && res.data) {
          const { data } = res;
          const obj = data.object ?? data;
          let raw = [];
          if (Array.isArray(data.data)) raw = data.data;
          else if (Array.isArray(data)) raw = data;
          else if (Array.isArray(obj?.data)) raw = obj.data;
          else if (Array.isArray(obj?.pageItems)) raw = obj.pageItems;
          else if (Array.isArray(obj)) raw = obj;

          this.commissions = raw.map(this.normalizeRow);
          const summary = obj?.summary ?? obj ?? data;
          const commVal = summary.commAmount ?? summary.commission ?? summary.comm_amount ?? data.commAmount ?? data.commission ?? data.comm_amount ?? 0;
          const whtxVal = summary.whtxAmount ?? summary.whTax ?? summary.whtx_amount ?? data.whtxAmount ?? data.whTax ?? data.whtx_amount ?? 0;
          const netVal = summary.netComm ?? summary.commisNet ?? summary.net_comm ?? data.netComm ?? data.commisNet ?? data.net_comm ?? 0;
          this.apiTotals = {
            commission: Number(commVal) || 0,
            whTax: Number(whtxVal) || 0,
            commisNet: Number(netVal) || 0,
          };
          if (raw.length > 0 && this.apiTotals.commission === 0 && this.apiTotals.whTax === 0 && this.apiTotals.commisNet === 0) {
            this.apiTotals = this.computeTotalsFromRows(this.commissions);
          }
        } else {
          this.commissions = [];
          this.apiTotals = { commission: 0, whTax: 0, commisNet: 0 };
          this.fetchError = 'Failed to load commission records.';
        }
      } catch (err) {
        this.$notify.fromError(err, 'Fetch Failed');
        this.commissions = [];
        this.apiTotals = { commission: 0, whTax: 0, commisNet: 0 };
        this.fetchError = 'Failed to load commission records.';
      } finally {
        this.loading = false;
      }
    },
    computeTotalsFromRows(rows) {
      return rows.reduce(
        (acc, row) => ({
          commission: acc.commission + (Number(row.commission) || 0),
          whTax: acc.whTax + (Number(row.whTax) || 0),
          commisNet: acc.commisNet + (Number(row.commisNet) || 0),
        }),
        { commission: 0, whTax: 0, commisNet: 0 },
      );
    },
    normalizeRow(row) {
      return {
        id: row.id,
        clientName: row.insured ?? row.clientName ?? row.client_name ?? '',
        policyNo: row.policyClaimNo ?? row.policyNo ?? row.policy_no ?? '',
        invoiceNo: row.debitNo ?? row.invoiceNo ?? row.invoice_no ?? '',
        receiptNo: row.receiptNo ?? row.receipt_no ?? '',
        receiptDate: row.transDate ?? row.receiptDate ?? row.receipt_date ?? '',
        grossPremium: row.grossPremium ?? row.gross_premium ?? 0,
        commission: row.commAmount ?? row.commission ?? 0,
        whTax: row.whtxAmount ?? row.whTax ?? row.wh_tax ?? 0,
        commisNet: row.netComm ?? row.commisNet ?? row.commis_net ?? 0,
        payable: row.payable ?? 0,
        chequeDate: row.chequeDate ?? row.cheque_date ?? row.chequeDt ?? row.chqDate ?? row.authDate ?? row.payDate ?? row.dateCheque ?? '',
      };
    },
    exportToPdf() {
      trackAgentInteraction(this, 'commissions_export_pdf', {
        rows: this.filteredCommissions.length,
      });
      try {
        const { jsPDF: JsPDF } = window.jspdf;
        const doc = new JsPDF('l', 'mm', 'a4');
        const pageW = doc.internal.pageSize.getWidth();
        const margin = 14;

        // Branding Header
        const logoY = 10;
        doc.addImage('/pacis-logo-tagline.png', 'PNG', margin, logoY, 38, 14);

        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.setFont('helvetica', 'normal');
        doc.text('Pacis Centre, 4th Floor, off Waiyaki Way', pageW - margin, logoY + 4, { align: 'right' });
        doc.text('P.O BOX 1870-00200, Nairobi, Kenya', pageW - margin, logoY + 8, { align: 'right' });
        doc.text('Email: info@paciskenya.com', pageW - margin, logoY + 12, { align: 'right' });

        // Divider
        doc.setDrawColor(230, 235, 245);
        doc.setLineWidth(0.5);
        doc.line(margin, 28, pageW - margin, 28);

        // Title
        doc.setFontSize(16);
        doc.setTextColor(30, 66, 118); // #1E4276
        doc.setFont('helvetica', 'bold');
        doc.text('COMMISSIONS REPORT', margin, 40);

        // Report Info Strip
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.setFont('helvetica', 'normal');
        const dateStr = `Period: ${this.toDdMmYyyy(this.appliedFilter.from)} to ${this.toDdMmYyyy(this.appliedFilter.to)}`;
        doc.text(dateStr, margin, 46);
        doc.text(`Status: ${this.appliedFilter.commissionStatus}`, margin, 51);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, 56);

        // Right side totals in summary box
        const boxW = 80;
        const boxX = pageW - margin - boxW;
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(boxX, 35, boxW, 25, 2, 2, 'F');
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.2);
        doc.roundedRect(boxX, 35, boxW, 25, 2, 2, 'S');

        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text('REPORT SUMMARY', boxX + 4, 40);

        const summaryRow = (label, val, y) => {
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100);
          doc.text(label, boxX + 4, y);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(30, 66, 118);
          doc.text(this.formatCurrency(val), boxX + boxW - 4, y, { align: 'right' });
        };

        summaryRow('Gross Commission:', this.totals.commission, 46);
        summaryRow('Widtholding Tax:', this.totals.whTax, 51);
        summaryRow('Net Commission:', this.totals.commisNet, 56);

        const head = [['Client Name', 'Policy No', 'Invoice No', 'Receipt No', 'Receipt Date', 'Commission', 'WH Tax', 'Net Commission', 'Cheque Date']];
        const body = this.filteredCommissions.map((t) => [
          t.clientName,
          t.policyNo,
          t.invoiceNo,
          t.receiptNo,
          t.receiptDate,
          this.formatCurrency(t.commission),
          this.formatCurrency(t.whTax),
          this.formatCurrency(t.commisNet),
          t.chequeDate || '—',
        ]);

        const tableTop = 65;
        if (typeof doc.autoTable === 'function') {
          doc.autoTable({
            startY: tableTop,
            head,
            body,
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: [30, 66, 118], fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            margin: { left: margin, right: margin },
          });
        } else if (window.jspdf && typeof window.jspdf.autoTable === 'function') {
          window.jspdf.autoTable(doc, {
            startY: tableTop,
            head,
            body,
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: [30, 66, 118], fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            margin: { left: margin, right: margin },
          });
        }

        // Validity / Computer Generated Note at bottom
        const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : tableTop + 20;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150);
        doc.text('This is a computer-generated report and does not require a signature.', margin, finalY);

        doc.save(`Commissions_Report_${this.appliedFilter.from}_to_${this.appliedFilter.to}.pdf`);
      } catch (err) {
        console.error('PDF Export Error:', err);
        if (this.$notify?.fromError) this.$notify.fromError(err, 'Export Failed');
      }
    },

  },
  metaInfo: { title: 'Commissions' },
};
</script>

<style scoped>
/* ── Header ── */
.page-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0 0 4px 0;
}
.page-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}

/* ── Totals Strip ── */
.totals-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.total-pill {
  background: white;
  border: 1px solid #E8EDF2;
  border-radius: 12px;
  padding: 14px 20px;
  display: flex;
  flex-direction: column;
  min-width: 160px;
  flex: 1;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.total-pill__label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.total-pill__value {
  font-size: 1.15rem;
  font-weight: 800;
  color: #1E4276;
}

/* ── Filters Card ── */
.filters-card {
  background: white;
  border: 1px solid #E8EDF2;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  justify-content: space-between;
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.date-input-wrapper {
  display: flex;
  align-items: center;
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 9px;
  padding: 0 12px;
  height: 44px;
  gap: 8px;
  transition: border-color 0.2s;
}

.date-input-wrapper:focus-within {
  border-color: #00AEEF;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.12);
}

.date-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  color: #1E4276;
  font-weight: 500;
}

.status-select {
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 9px;
  padding: 0 12px;
  height: 44px;
  font-size: 0.9rem;
  color: #1E4276;
  font-weight: 500;
  min-width: 140px;
  cursor: pointer;
  transition: border-color 0.2s;
}

.status-select:focus {
  outline: none;
  border-color: #00AEEF;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.12);
}

.btn-apply {
  background: #00AEEF;
  color: white;
  border: none;
  padding: 0 20px;
  height: 44px;
  border-radius: 9px;
  font-weight: 700;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: opacity 0.2s;
  box-shadow: 0 2px 8px rgba(0, 174, 239, 0.25);
  white-space: nowrap;
}

.btn-apply:hover:not(:disabled) { opacity: 0.9; }
.btn-apply:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-export-pdf {
  background: #E53E3E;
  color: white;
  border: none;
  padding: 0 20px;
  height: 44px;
  border-radius: 9px;
  font-weight: 700;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: opacity 0.2s;
  box-shadow: 0 2px 8px rgba(229, 62, 62, 0.25);
  white-space: nowrap;
}
.btn-export-pdf:hover:not(:disabled) { opacity: 0.9; }
.btn-export-pdf:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-export-excel {
  background: #10B981;
  color: white;
  border: border;
  padding: 0 20px;
  height: 44px;
  border-radius: 9px;
  font-weight: 700;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: opacity 0.2s;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);
  white-space: nowrap;
}
.btn-export-excel:hover:not(:disabled) { opacity: 0.9; }
.btn-export-excel:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Search ── */
.search-wrapper {
  display: flex;
  align-items: center;
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 9px;
  padding: 0 12px;
  height: 44px;
  min-width: 240px;
  gap: 8px;
  transition: border-color 0.2s;
}

.search-wrapper:focus-within {
  border-color: #00AEEF;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.12);
  background: white;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  color: #1E4276;
}

.search-input::placeholder { color: #94A3B8; }

.search-clear {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
}

/* ── Table Card ── */
.table-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #E8EDF2;
  box-shadow: 0 4px 24px rgba(0,0,0,0.06);
  overflow: hidden;
}

.table-scroll {
  overflow-x: auto;
}

/* ── Table ── */
.comm-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 960px;
}

.comm-table thead tr {
  background: #F8FAFC;
  border-bottom: 2px solid #E8EDF2;
}

.comm-table th {
  text-align: left;
  padding: 14px 16px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.comm-row {
  border-bottom: 1px solid #F1F5F9;
  transition: background 0.15s;
}

.comm-row:last-child { border-bottom: none; }
.comm-row:hover { background: #F0F9FF; }

.comm-table td {
  padding: 14px 16px;
  vertical-align: middle;
  font-size: 0.875rem;
}

.client-name {
  font-weight: 700;
  color: #1E4276;
}

.mono-text {
  font-size: 0.82rem;
  color: #64748B;
  font-family: monospace;
  background: #F1F5F9;
  padding: 2px 7px;
  border-radius: 5px;
}

.cell-text { color: #334155; }
.cell-muted { color: #94A3B8; font-size: 0.82rem; }

.commission-value { color: #00AEEF; font-weight: 700; }
.tax-value        { color: #EF4444; font-weight: 600; }
.net-value        { color: #10b981; font-weight: 800; }

/* ── Totals Footer ── */
.totals-row {
  background: #1E4276;
}

.totals-row td {
  padding: 16px;
}

.totals-label {
  font-weight: 800;
  color: rgba(255,255,255,0.7);
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: right;
}

.totals-cell {
  font-weight: 800;
  color: white;
  font-size: 0.9rem;
}

.totals-cell--net {
  color: #FFD700;
}

/* ── Empty State ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}

.empty-icon-wrap {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: #EBF8FF;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0 0 6px;
}

.empty-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}
/* ── Commissions Shimmer Loading ── */

.commissions-loading {
  width: 100%;
}

/* Base Shimmer */

.shimmer {
  position: relative;
  overflow: hidden;
  background: #E9EEF3;
  border-radius: 6px;
}

.shimmer::after {
  content: '';
  position: absolute;
  top: 0;
  left: -150%;
  width: 150%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.65) 50%,
    transparent 100%
  );
  animation: commissions-shimmer 1.5s infinite;
}

@keyframes commissions-shimmer {
  0% {
    left: -150%;
  }

  100% {
    left: 150%;
  }
}

/* ── Summary Totals ── */

.commission-total-shimmer {
  background: white;
  border: 1px solid #E8EDF2;
  border-radius: 12px;
  padding: 14px 20px;
  min-width: 160px;
  flex: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.shimmer-total-label {
  width: 125px;
  height: 11px;
  margin-bottom: 9px;
}

.shimmer-total-value {
  width: 90px;
  height: 19px;
}

/* ── Commission Table ── */

.commission-shimmer-card {
  overflow: hidden;
}

.commission-shimmer-table {
  width: 100%;
  overflow-x: auto;
}

.commission-shimmer-header,
.commission-shimmer-row,
.commission-shimmer-footer {
  display: grid;
  grid-template-columns:
    minmax(150px, 1.4fr)
    minmax(110px, 1fr)
    minmax(110px, 1fr)
    minmax(110px, 1fr)
    minmax(120px, 1fr)
    minmax(110px, 1fr)
    minmax(100px, 0.9fr)
    minmax(120px, 1fr)
    minmax(120px, 1fr);
  align-items: center;
  column-gap: 16px;
  min-width: 1100px;
  padding-left: 16px;
  padding-right: 16px;
}

.commission-shimmer-header {
  min-height: 48px;
  background: #F8FAFC;
  border-bottom: 2px solid #E8EDF2;
}

.commission-shimmer-row {
  min-height: 64px;
  border-bottom: 1px solid #F1F5F9;
}

/* ── Header Placeholders ── */

.shimmer-header-client {
  width: 85px;
  height: 11px;
}

.shimmer-header-policy {
  width: 70px;
  height: 11px;
}

.shimmer-header-invoice {
  width: 70px;
  height: 11px;
}

.shimmer-header-receipt {
  width: 70px;
  height: 11px;
}

.shimmer-header-date {
  width: 80px;
  height: 11px;
}

.shimmer-header-amount {
  width: 75px;
  height: 11px;
  margin-left: auto;
}

/* ── Row Placeholders ── */

.shimmer-client {
  width: 125px;
  height: 15px;
}

.shimmer-policy {
  width: 95px;
  height: 22px;
  border-radius: 5px;
}

.shimmer-invoice {
  width: 95px;
  height: 22px;
  border-radius: 5px;
}

.shimmer-receipt {
  width: 95px;
  height: 22px;
  border-radius: 5px;
}

.shimmer-date {
  width: 85px;
  height: 14px;
}

.shimmer-amount {
  width: 85px;
  height: 15px;
  margin-left: auto;
}

/* ── Totals Footer ── */

.commission-shimmer-footer {
  min-height: 64px;
  display: grid;
  grid-template-columns:
    minmax(150px, 1.4fr)
    minmax(110px, 1fr)
    minmax(110px, 1fr)
    minmax(110px, 1fr)
    minmax(120px, 1fr)
    minmax(110px, 1fr)
    minmax(100px, 0.9fr)
    minmax(120px, 1fr)
    minmax(120px, 1fr);
  align-items: center;
  column-gap: 16px;
  min-width: 1100px;
  padding: 0 16px;
  background: #1E4276;
}

.shimmer-footer-label {
  width: 55px;
  height: 12px;
  background: rgba(255, 255, 255, 0.25);
}

.shimmer-footer-value {
  width: 85px;
  height: 15px;
  margin-left: auto;
  background: rgba(255, 255, 255, 0.25);
}
</style>
